import React, {useEffect, useState} from 'react';
import styles from '../../styles/dashboard.module.css';
import Header from "../../components/header/header";
import PageTitle from "../../components/page_title/page_title";
import GPSTabs from "./components/gps_tabs";
import axiosInstance from "../../AxiosInstance";
import Modal from "../../components/modal/modal";
import {TextInput} from "../../components/input/inputs";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faUser, faBox, faTrash, faPlus, faList, faGrip, faPaperPlane} from "@fortawesome/free-solid-svg-icons";
import {useTranslation} from "react-i18next";

const STALE_THRESHOLD_SEC = 300; // 5 minutes

const inputStyle = {
    width: '100%', padding: '7px 10px', borderRadius: '6px',
    border: '1px solid #CBD5E1', fontSize: '13px',
    outline: 'none', marginBottom: '4px', boxSizing: 'border-box',
};

const EMPTY_SEND_STATE = {
    target_mode: 'all',
    target_group_id: '',
    selected_asset_ids: [],
    // map of device_type_id (str) -> selected gps_action_id (int|null)
    selectedActionPerType: {},
    // map of device_type_id (str) -> {data_1, data_2, data_3} overrides
    overridesPerType: {},
    // set of device_type_id (str) where detail is expanded
    expandedTypes: new Set(),
};

function getSecondsSince(isoString) {
    if (!isoString) return Infinity;
    return (Date.now() - new Date(isoString).getTime()) / 1000;
}

function formatLastSeen(isoString) {
    if (!isoString) return 'Never';
    const sec = Math.floor(getSecondsSince(isoString));
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    const d = new Date(isoString);
    return d.toLocaleDateString(undefined, {day: '2-digit', month: '2-digit', year: '2-digit'}) +
        ' ' + d.toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit'});
}

function isOnline(isoString) {
    return getSecondsSince(isoString) <= STALE_THRESHOLD_SEC;
}

export default function GPSAssets() {
    const {t} = useTranslation();
    const project_id = parseInt(localStorage.getItem('project_id'));
    const [assets, setAssets] = useState([]);
    const [groups, setGroups] = useState([]);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [groupForm, setGroupForm] = useState({name: ''});
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [showMembersModal, setShowMembersModal] = useState(false);
    const [memberSearch, setMemberSearch] = useState('');
    const [pendingMemberIds, setPendingMemberIds] = useState(new Set());
    const [assetSearch, setAssetSearch] = useState('');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
    const [, forceUpdate] = useState(0);

    // Send action state
    const [showSendAction, setShowSendAction] = useState(false);
    const [sendState, setSendState] = useState(EMPTY_SEND_STATE);
    const [sendLoading, setSendLoading] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const [selectSearch, setSelectSearch] = useState('');
    const [gpsActions, setGpsActions] = useState([]);

    // Re-render every 30s so "X ago" labels stay current
    useEffect(() => {
        const timer = setInterval(() => forceUpdate(n => n + 1), 30000);
        return () => clearInterval(timer);
    }, []);

    const fetchData = (mounted) => {
        if (!project_id) return;
        axiosInstance.get(`gps_asset/?project_id=${project_id}`).then(res => { if (mounted.current) setAssets(res.data); });
        axiosInstance.get(`gps_asset_group/?project_id=${project_id}`).then(res => { if (mounted.current) setGroups(res.data); });
        axiosInstance.get(`gps_action/?project_id=${project_id}`).then(res => { if (mounted.current) setGpsActions(Array.isArray(res.data) ? res.data : []); }).catch(() => {});
    };

    useEffect(() => {
        const mounted = {current: true};
        fetchData(mounted);
        return () => { mounted.current = false; };
    }, [project_id]);

    // Auto-select the only available device for assets that have no active device
    useEffect(() => {
        if (!assets || assets.length === 0) return;
        const needsAutoSelect = assets.filter(
            a => !a.active_gps_device && a.gps_devices?.length === 1
        );
        if (needsAutoSelect.length === 0) return;
        Promise.all(
            needsAutoSelect.map(asset =>
                axiosInstance.post(`gps_asset/${asset.id}/set_active_device/`, {device_id: asset.gps_devices[0].id})
            )
        ).then(fetchData);
    }, [assets]);

    const setActiveDevice = (assetId, deviceId) => {
        axiosInstance.post(`gps_asset/${assetId}/set_active_device/`, {device_id: deviceId}).then(() => {
            fetchData();
        });
    };

    const createGroup = () => {
        axiosInstance.post('gps_asset_group/', {
            name: groupForm.name,
            project: project_id,
            member_ids: []
        }).then(() => {
            setShowGroupModal(false);
            setGroupForm({name: ''});
            fetchData();
        });
    };

    const deleteGroup = (groupId) => {
        axiosInstance.delete(`gps_asset_group/${groupId}/`).then(fetchData);
    };

    const openMembersModal = (group) => {
        const liveGroup = groups.find(g => g.id === group.id);
        setPendingMemberIds(new Set(liveGroup?.members?.map(m => m.device_user_id) || []));
        setSelectedGroup(group);
        setShowMembersModal(true);
    };

    const togglePendingMember = (assetId) => {
        setPendingMemberIds(prev => {
            const next = new Set(prev);
            if (next.has(assetId)) next.delete(assetId);
            else next.add(assetId);
            return next;
        });
    };

    const saveMembers = () => {
        if (!selectedGroup) return;
        axiosInstance.put(`gps_asset_group/${selectedGroup.id}/`, {
            name: selectedGroup.name,
            project: project_id,
            member_ids: Array.from(pendingMemberIds)
        }).then(() => {
            setShowMembersModal(false);
            setMemberSearch('');
            fetchData();
        });
    };

    const toggleSelectedAsset = (assetId) => {
        setSendState(prev => {
            const ids = prev.selected_asset_ids;
            if (ids.includes(assetId)) return {...prev, selected_asset_ids: ids.filter(i => i !== assetId)};
            return {...prev, selected_asset_ids: [...ids, assetId]};
        });
    };

    const openSendAction = () => {
        setSendState(EMPTY_SEND_STATE);
        setSendResult(null);
        setSelectSearch('');
        setShowSendAction(true);
    };

    // Returns list of {device_type_id, device_type_name, asset_ids} for selected targets
    const getTargetGroups = () => {
        let targetAssets = [];
        if (sendState.target_mode === 'all') {
            targetAssets = assets;
        } else if (sendState.target_mode === 'group') {
            const group = groups.find(g => String(g.id) === String(sendState.target_group_id));
            if (group) {
                const memberIds = new Set(group.members?.map(m => m.device_user_id) || []);
                targetAssets = assets.filter(a => memberIds.has(a.id));
            }
        } else if (sendState.target_mode === 'select') {
            const ids = new Set(sendState.selected_asset_ids);
            targetAssets = assets.filter(a => ids.has(a.id));
        }

        // Group by device_type_id
        const byType = {};
        for (const asset of targetAssets) {
            const dtId = asset.active_gps_device?.device_type_id;
            const dtName = asset.active_gps_device?.device_type_name || t('gps.assets.unknownType');
            if (dtId == null) continue;
            const key = String(dtId);
            if (!byType[key]) byType[key] = {device_type_id: key, device_type_name: dtName, asset_ids: []};
            byType[key].asset_ids.push(asset.id);
        }
        return Object.values(byType);
    };

    const handleSendAction = async () => {
        if (sendLoading) return;
        setSendLoading(true);
        setSendResult(null);

        // Validate targets
        if (sendState.target_mode === 'group' && !sendState.target_group_id) {
            setSendResult({error: t('gps.assets.noGroupSelected')});
            setSendLoading(false);
            return;
        }

        const targetGroups = getTargetGroups();
        if (targetGroups.length === 0) {
            setSendResult({error: t('gps.assets.noTargetsSelected')});
            setSendLoading(false);
            return;
        }

        // Validate that each device_type group has an action selected
        for (const group of targetGroups) {
            if (!sendState.selectedActionPerType[group.device_type_id]) {
                setSendResult({error: t('gps.assets.noActionSelected', {deviceType: group.device_type_name})});
                setSendLoading(false);
                return;
            }
        }

        // Send one request per device_type group
        let totalExecuted = 0;
        const allErrors = [];
        try {
            for (const group of targetGroups) {
                const actionId = sendState.selectedActionPerType[group.device_type_id];
                const overrides = sendState.overridesPerType[group.device_type_id] || {};
                const res = await axiosInstance.post('gps_asset/send_action/', {
                    gps_action_id: actionId,
                    asset_ids: group.asset_ids,
                    group_ids: [],
                    ...(overrides.data_1 !== undefined ? {data_1_override: overrides.data_1} : {}),
                    ...(overrides.data_2 !== undefined ? {data_2_override: overrides.data_2} : {}),
                    ...(overrides.data_3 !== undefined ? {data_3_override: overrides.data_3} : {}),
                });
                totalExecuted += res.data.executed || 0;
                if (res.data.errors?.length) allErrors.push(...res.data.errors);
            }
            setSendResult({executed: totalExecuted, errors: allErrors});
            if (!allErrors.length) {
                setTimeout(() => setShowSendAction(false), 1200);
            }
        } catch (e) {
            setSendResult({error: e.response?.data?.error || 'Error sending action'});
        }
        setSendLoading(false);
    };

    // Filter assets for main list by name, DNI, or device serial number
    const filteredAssets = assets.filter(asset => {
        const q = assetSearch.toLowerCase().trim();
        if (!q) return true;
        if (asset.name?.toLowerCase().includes(q)) return true;
        if (asset.identification_number?.toLowerCase().includes(q)) return true;
        if (asset.gps_devices?.some(d => d.sn?.toLowerCase().includes(q))) return true;
        return false;
    });

    // Filter assets for manage members modal by name or DNI
    const filteredMemberAssets = assets.filter(asset => {
        const q = memberSearch.toLowerCase().trim();
        if (!q) return true;
        if (asset.name?.toLowerCase().includes(q)) return true;
        if (asset.identification_number?.toLowerCase().includes(q)) return true;
        return false;
    });

    // Filter assets for the "select individually" section
    const filteredSelectAssets = assets.filter(asset => {
        const q = selectSearch.toLowerCase().trim();
        if (!q) return true;
        if (asset.name?.toLowerCase().includes(q)) return true;
        if (asset.identification_number?.toLowerCase().includes(q)) return true;
        return false;
    });

    const cardStyle = {
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #E2E8F0',
    };

    const renderSendActionForm = () => {
        const targetGroups = getTargetGroups();
        const multipleTypes = targetGroups.length > 1;

        const renderActionSelector = (dtId, dtName, dtAssetIds) => {
            const actionsForType = gpsActions.filter(a => String(a.device_type) === String(dtId));
            const selectedId = sendState.selectedActionPerType[String(dtId)];
            const selectedAction = actionsForType.find(a => a.id === selectedId);
            const overrides = sendState.overridesPerType[String(dtId)] || {};
            const expanded = sendState.expandedTypes.has(String(dtId));

            const setSelectedAction = (id) => {
                const action = actionsForType.find(a => a.id === parseInt(id));
                setSendState(s => ({
                    ...s,
                    selectedActionPerType: {...s.selectedActionPerType, [String(dtId)]: id ? parseInt(id) : null},
                    overridesPerType: {...s.overridesPerType, [String(dtId)]: action ? {data_1: action.data_1 || '', data_2: action.data_2 || '', data_3: action.data_3 || ''} : {}},
                }));
            };

            const setOverride = (field, value) => {
                setSendState(s => ({
                    ...s,
                    overridesPerType: {...s.overridesPerType, [String(dtId)]: {...(s.overridesPerType[String(dtId)] || {}), [field]: value}},
                }));
            };

            const toggleExpanded = () => {
                setSendState(s => {
                    const next = new Set(s.expandedTypes);
                    if (next.has(String(dtId))) next.delete(String(dtId));
                    else next.add(String(dtId));
                    return {...s, expandedTypes: next};
                });
            };

            return (
                <div key={dtId} style={{marginBottom: '16px', padding: '12px', border: '1px solid #E2E8F0', borderRadius: '8px', background: '#FAFAFA'}}>
                    {multipleTypes && (
                        <div style={{fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '8px'}}>
                            {dtName} <span style={{color: '#94A3B8', fontWeight: 400}}>({dtAssetIds.length} {dtAssetIds.length === 1 ? t('gps.assets.asset') : t('gps.assets.assetsLower')})</span>
                        </div>
                    )}
                    <select
                        value={selectedId || ''}
                        onChange={e => setSelectedAction(e.target.value)}
                        style={inputStyle}
                    >
                        <option value="">{t('gps.assets.selectAction')}</option>
                        {actionsForType.length === 0 && (
                            <option disabled>{t('gps.assets.noActionsForType')}</option>
                        )}
                        {actionsForType.map(a => (
                            <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                    </select>

                    {selectedAction && (
                        <div style={{marginTop: '6px'}}>
                            <button
                                type="button"
                                onClick={toggleExpanded}
                                style={{background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '12px', color: '#4F46E5', fontWeight: '500'}}
                            >
                                {expanded ? t('gps.assets.hideDetails') : t('gps.assets.showDetails')}
                            </button>
                            {expanded && (
                                <div style={{marginTop: '8px', padding: '10px', background: 'white', border: '1px solid #E2E8F0', borderRadius: '6px'}}>
                                    {(selectedAction.action_type === 5 || selectedAction.action_type === 1) && (
                                        <>
                                            <div style={{fontSize: '11px', fontWeight: '600', color: '#64748B', marginBottom: '3px'}}>
                                                {selectedAction.action_type === 5 ? t('gps.assets.topic') : t('gps.assets.emailAddress')}
                                            </div>
                                            <input
                                                value={overrides.data_1 !== undefined ? overrides.data_1 : (selectedAction.data_1 || '')}
                                                onChange={e => setOverride('data_1', e.target.value)}
                                                style={{...inputStyle, fontFamily: 'monospace', fontSize: '12px'}}
                                            />
                                        </>
                                    )}
                                    {selectedAction.action_type === 6 && (
                                        <>
                                            <div style={{fontSize: '11px', fontWeight: '600', color: '#64748B', marginBottom: '3px'}}>{t('gps.assets.endpointPath')}</div>
                                            <input
                                                value={overrides.data_1 !== undefined ? overrides.data_1 : (selectedAction.data_1 || '')}
                                                onChange={e => setOverride('data_1', e.target.value)}
                                                style={{...inputStyle, fontFamily: 'monospace', fontSize: '12px'}}
                                            />
                                        </>
                                    )}
                                    {(selectedAction.action_type === 5 || selectedAction.action_type === 6) && (
                                        <>
                                            <div style={{fontSize: '11px', fontWeight: '600', color: '#64748B', marginBottom: '3px', marginTop: '8px'}}>
                                                {selectedAction.action_type === 5 ? t('gps.assets.payload') : t('gps.assets.bodyJson')}
                                            </div>
                                            <textarea
                                                value={overrides.data_3 !== undefined ? overrides.data_3 : (selectedAction.data_3 || '')}
                                                onChange={e => setOverride('data_3', e.target.value)}
                                                style={{...inputStyle, minHeight: '70px', resize: 'vertical', fontFamily: 'monospace', fontSize: '12px'}}
                                            />
                                        </>
                                    )}
                                    {(selectedAction.action_type === 1) && (
                                        <>
                                            <div style={{fontSize: '11px', fontWeight: '600', color: '#64748B', marginBottom: '3px', marginTop: '8px'}}>{t('gps.assets.subject')}</div>
                                            <input
                                                value={overrides.data_2 !== undefined ? overrides.data_2 : (selectedAction.data_2 || '')}
                                                onChange={e => setOverride('data_2', e.target.value)}
                                                style={{...inputStyle, fontSize: '12px'}}
                                            />
                                            <div style={{fontSize: '11px', fontWeight: '600', color: '#64748B', marginBottom: '3px', marginTop: '8px'}}>{t('gps.assets.body')}</div>
                                            <textarea
                                                value={overrides.data_3 !== undefined ? overrides.data_3 : (selectedAction.data_3 || '')}
                                                onChange={e => setOverride('data_3', e.target.value)}
                                                style={{...inputStyle, minHeight: '60px', resize: 'vertical', fontSize: '12px'}}
                                            />
                                        </>
                                    )}
                                    {(selectedAction.action_type === 3 || selectedAction.action_type === 4) && (
                                        <>
                                            <div style={{fontSize: '11px', fontWeight: '600', color: '#64748B', marginBottom: '3px'}}>{t('gps.assets.phoneNumber')}</div>
                                            <input
                                                value={overrides.data_1 !== undefined ? overrides.data_1 : (selectedAction.data_1 || '')}
                                                onChange={e => setOverride('data_1', e.target.value)}
                                                style={{...inputStyle, fontSize: '12px'}}
                                            />
                                            <div style={{fontSize: '11px', fontWeight: '600', color: '#64748B', marginBottom: '3px', marginTop: '8px'}}>{t('gps.assets.message')}</div>
                                            <textarea
                                                value={overrides.data_3 !== undefined ? overrides.data_3 : (selectedAction.data_3 || '')}
                                                onChange={e => setOverride('data_3', e.target.value)}
                                                style={{...inputStyle, minHeight: '60px', resize: 'vertical', fontSize: '12px'}}
                                            />
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        };

        return (
        <div>
            {/* Targets */}
            <div style={{marginBottom: '16px'}}>
                <div style={{fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '10px'}}>{t('gps.assets.targets')}</div>
                {[
                    {value: 'all', label: t('gps.assets.allAssets', {count: assets.length})},
                    {value: 'group', label: t('gps.assets.byGroup')},
                    {value: 'select', label: t('gps.assets.selectIndividually')},
                ].map(opt => (
                    <label key={opt.value} style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer', fontSize: '13px'}}>
                        <input
                            type="radio"
                            name="send_target_mode"
                            value={opt.value}
                            checked={sendState.target_mode === opt.value}
                            onChange={() => setSendState(s => ({...s, target_mode: opt.value}))}
                            style={{accentColor: '#4F46E5'}}
                        />
                        {opt.label}
                    </label>
                ))}

                {sendState.target_mode === 'group' && (
                    <select
                        value={sendState.target_group_id}
                        onChange={e => setSendState(s => ({...s, target_group_id: e.target.value}))}
                        style={inputStyle}
                    >
                        <option value="">{t('gps.assets.selectGroup')}</option>
                        {groups.map(g => (
                            <option key={g.id} value={g.id}>{g.name} ({g.members?.length || 0} members)</option>
                        ))}
                    </select>
                )}

                {sendState.target_mode === 'select' && (
                    <div style={{border: '1px solid #E2E8F0', borderRadius: '6px', overflow: 'hidden', marginTop: '6px'}}>
                        <input
                            type="text"
                            placeholder={t('gps.assets.searchAssets')}
                            value={selectSearch}
                            onChange={e => setSelectSearch(e.target.value)}
                            style={{...inputStyle, borderRadius: 0, border: 'none', borderBottom: '1px solid #E2E8F0', marginBottom: 0}}
                        />
                        <div style={{maxHeight: '180px', overflowY: 'auto'}}>
                            {filteredSelectAssets.map(asset => {
                                const selected = sendState.selected_asset_ids.includes(asset.id);
                                return (
                                    <div
                                        key={asset.id}
                                        onClick={() => toggleSelectedAsset(asset.id)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '8px',
                                            padding: '8px 10px', cursor: 'pointer',
                                            borderBottom: '1px solid #F1F5F9',
                                            background: selected ? '#EEF2FF' : 'white',
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selected}
                                            onChange={() => toggleSelectedAsset(asset.id)}
                                            onClick={e => e.stopPropagation()}
                                            style={{accentColor: '#4F46E5', width: '15px', height: '15px'}}
                                        />
                                        <FontAwesomeIcon icon={asset.is_human ? faUser : faBox} color="#64748B" size="sm"/>
                                        <div>
                                            <div style={{fontSize: '13px', fontWeight: '500'}}>{asset.name}</div>
                                            {asset.identification_number && (
                                                <div style={{fontSize: '11px', color: '#94A3B8'}}>{asset.identification_number}</div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {sendState.selected_asset_ids.length > 0 && (
                            <div style={{padding: '6px 10px', fontSize: '12px', color: '#4F46E5', background: '#F5F3FF', borderTop: '1px solid #E2E8F0'}}>
                                {t('gps.assets.selectedCount', {count: sendState.selected_asset_ids.length})}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Divider */}
            <div style={{borderTop: '1px solid #E2E8F0', marginBottom: '16px'}}/>

            {/* Action selection */}
            <div style={{fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '10px'}}>{t('gps.assets.action')}</div>

            {targetGroups.length === 0 && (
                <div style={{fontSize: '13px', color: '#94A3B8', padding: '8px 0'}}>{t('gps.assets.selectTargetsFirst')}</div>
            )}

            {targetGroups.length > 1 && (
                <div style={{marginBottom: '12px', padding: '8px 12px', borderRadius: '6px', background: '#FFFBEB', border: '1px solid #FDE68A', fontSize: '12px', color: '#92400E'}}>
                    {t('gps.assets.multipleDeviceTypesWarning', {count: targetGroups.length})}
                </div>
            )}

            {targetGroups.map(g => renderActionSelector(g.device_type_id, g.device_type_name, g.asset_ids))}

            {/* Result feedback */}
            {sendResult && (
                <div style={{
                    marginTop: '12px', padding: '10px 12px', borderRadius: '6px',
                    backgroundColor: sendResult.error ? '#FEF2F2' : '#F0FDF4',
                    border: `1px solid ${sendResult.error ? '#FECACA' : '#BBF7D0'}`,
                    fontSize: '13px',
                    color: sendResult.error ? '#DC2626' : '#16A34A',
                }}>
                    {sendResult.error
                        ? sendResult.error
                        : `${t('gps.assets.actionSentTo')} ${sendResult.executed} ${sendResult.executed !== 1 ? t('gps.assets.assetsLower') : t('gps.assets.assetLower')}${sendResult.errors?.length ? ` (${sendResult.errors.length} error${sendResult.errors.length !== 1 ? 's' : ''})` : ''}`
                    }
                    {sendResult.errors?.length > 0 && (
                        <ul style={{margin: '6px 0 0', paddingLeft: '16px'}}>
                            {sendResult.errors.map((e, i) => (
                                <li key={i}>{e.asset}: {e.error}</li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
        );
    };

    const renderAssetCard = (asset) => {
        const lastMsg = asset.active_gps_device?.last_message;
        const online = isOnline(lastMsg);
        return (
            <div key={asset.id} style={{...cardStyle, width: 'min(280px, 100%)'}}>
                <div className="d-flex align-items-center gap-2 mb-2">
                    <div style={{
                        width: '32px', height: '32px', borderRadius: '50%',
                        backgroundColor: asset.is_human ? '#10B981' : '#F59E0B',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <FontAwesomeIcon icon={asset.is_human ? faUser : faBox} color="white" size="sm"/>
                    </div>
                    <div style={{flex: 1}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                            <div
                                style={{
                                    width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0,
                                    backgroundColor: online ? '#22C55E' : '#EF4444',
                                }}
                                title={online ? t('gps.assets.online') : t('gps.assets.noSignal')}
                            />
                            <div style={{fontWeight: '600', fontSize: '14px'}}>{asset.name}</div>
                        </div>
                        <div style={{fontSize: '12px', color: '#64748B'}}>
                            {asset.is_human ? t('gps.assets.person') : t('gps.assets.asset')}
                            {asset.identification_number && ` · ${asset.identification_number}`}
                        </div>
                    </div>
                </div>

                <div style={{fontSize: '12px', color: '#64748B', marginBottom: '4px'}}>{t('gps.assets.activeGpsDevice')}</div>
                <select
                    value={asset.active_gps_device?.id || ''}
                    onChange={e => setActiveDevice(asset.id, parseInt(e.target.value))}
                    style={{
                        width: '100%', padding: '6px 8px', borderRadius: '4px',
                        border: '1px solid #CBD5E1', fontSize: '13px'
                    }}
                >
                    <option value="">{t('gps.assets.noneDevice')}</option>
                    {asset.gps_devices?.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                </select>

                <div style={{fontSize: '11px', color: '#94A3B8', marginTop: '6px', display: 'flex', justifyContent: 'space-between'}}>
                    <span>{asset.current_position ? `${asset.current_position.lat.toFixed(4)}, ${asset.current_position.lng.toFixed(4)}` : ''}</span>
                    <span title={lastMsg ? new Date(lastMsg).toLocaleString() : ''}>
                        {lastMsg ? formatLastSeen(lastMsg) : t('gps.assets.noData')}
                    </span>
                </div>
            </div>
        );
    };

    const renderAssetRow = (asset) => {
        const lastMsg = asset.active_gps_device?.last_message;
        const online = isOnline(lastMsg);
        return (
            <tr key={asset.id} style={{borderBottom: '1px solid #F1F5F9'}}>
                <td style={{padding: '10px 12px'}}>
                    <div className="d-flex align-items-center gap-2">
                        <div style={{
                            width: '28px', height: '28px', borderRadius: '50%',
                            backgroundColor: asset.is_human ? '#10B981' : '#F59E0B',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <FontAwesomeIcon icon={asset.is_human ? faUser : faBox} color="white" size="sm"/>
                        </div>
                        <div
                            style={{
                                width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0,
                                backgroundColor: online ? '#22C55E' : '#EF4444',
                            }}
                            title={online ? t('gps.assets.online') : t('gps.assets.noSignal')}
                        />
                        <span style={{fontWeight: '500', fontSize: '14px'}}>{asset.name}</span>
                    </div>
                </td>
                <td style={{padding: '10px 12px', fontSize: '13px', color: '#64748B'}}>
                    {asset.identification_number || '—'}
                </td>
                <td style={{padding: '10px 12px', fontSize: '13px'}}>
                    <select
                        value={asset.active_gps_device?.id || ''}
                        onChange={e => setActiveDevice(asset.id, parseInt(e.target.value))}
                        style={{
                            padding: '4px 6px', borderRadius: '4px',
                            border: '1px solid #CBD5E1', fontSize: '13px'
                        }}
                    >
                        <option value="">{t('gps.assets.noneDevice')}</option>
                        {asset.gps_devices?.map(d => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                    </select>
                </td>
                <td style={{padding: '10px 12px', fontSize: '12px', color: '#94A3B8'}}>
                    {asset.current_position
                        ? `${asset.current_position.lat.toFixed(4)}, ${asset.current_position.lng.toFixed(4)}`
                        : '—'}
                </td>
                <td style={{padding: '10px 12px', fontSize: '12px', color: '#94A3B8'}}
                    title={lastMsg ? new Date(lastMsg).toLocaleString() : ''}>
                    {lastMsg ? formatLastSeen(lastMsg) : '—'}
                </td>
            </tr>
        );
    };

    return (
        <div className={styles.dashboard_container}>
            <Header sidebarIndex={1}/>

            <Modal show={showGroupModal} setter={setShowGroupModal} name={t('gps.assets.createGroup')} buttonName={t('common.create')} onClick={createGroup}>
                <TextInput form={groupForm} setForm={setGroupForm} name={'name'} placeholder={t('gps.assets.groupName')}/>
            </Modal>

            <Modal
                show={showMembersModal}
                setter={(val) => { setShowMembersModal(val); if (!val) setMemberSearch(''); }}
                name={t('gps.assets.members', {name: selectedGroup?.name || ''})}
                buttonName={t('common.save')}
                onClick={saveMembers}
            >
                {/* Search inside modal */}
                <input
                    type="text"
                    placeholder={t('gps.assets.searchMembers')}
                    value={memberSearch}
                    onChange={e => setMemberSearch(e.target.value)}
                    style={{
                        width: '100%', padding: '7px 10px', marginBottom: '10px',
                        border: '1px solid #CBD5E1', borderRadius: '6px', fontSize: '13px',
                        outline: 'none',
                    }}
                />
                <div style={{maxHeight: '300px', overflowY: 'auto'}}>
                    {filteredMemberAssets.map(asset => {
                        const isMember = pendingMemberIds.has(asset.id);
                        return (
                            <div
                                key={asset.id}
                                className="d-flex align-items-center justify-content-between p-2"
                                style={{borderBottom: '1px solid #F1F5F9', cursor: 'pointer'}}
                                onClick={() => togglePendingMember(asset.id)}
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={asset.is_human ? faUser : faBox} color="#64748B"/>
                                    <div>
                                        <div style={{fontSize: '14px'}}>{asset.name}</div>
                                        {asset.identification_number && (
                                            <div style={{fontSize: '11px', color: '#94A3B8'}}>{asset.identification_number}</div>
                                        )}
                                    </div>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={isMember}
                                    onChange={() => togglePendingMember(asset.id)}
                                    style={{width: '16px', height: '16px', cursor: 'pointer', accentColor: '#10B981'}}
                                    onClick={e => e.stopPropagation()}
                                />
                            </div>
                        );
                    })}
                </div>
            </Modal>

            <Modal
                show={showSendAction}
                setter={(val) => { setShowSendAction(val); if (!val) { setSendResult(null); setSelectSearch(''); } }}
                name={t('gps.assets.sendAction')}
                buttonName={sendLoading ? t('gps.assets.sending') : t('common.send')}
                onClick={handleSendAction}
            >
                {renderSendActionForm()}
            </Modal>

            <div className={styles.dashboardInnerContainer}>
                <PageTitle title={t('gps.assets.title')}/>
                <GPSTabs active={1}/>

                {/* Assets List */}
                <div style={{marginBottom: '24px'}}>
                    <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-3 gap-2">
                        <h6 style={{fontWeight: '600', margin: 0, flexShrink: 0}}>{t('gps.assets.assetsAndPersons')}</h6>
                        <div className="d-flex align-items-center gap-2 flex-wrap" style={{width: '100%', maxWidth: '520px'}}>
                            <input
                                type="text"
                                placeholder={t('gps.assets.searchPlaceholder')}
                                value={assetSearch}
                                onChange={e => setAssetSearch(e.target.value)}
                                style={{
                                    padding: '6px 10px', border: '1px solid #CBD5E1',
                                    borderRadius: '6px', fontSize: '13px', outline: 'none',
                                    flex: '1', minWidth: '120px',
                                }}
                            />
                            <button
                                onClick={openSendAction}
                                title="Send action to assets"
                                style={{
                                    padding: '6px 12px', border: 'none', borderRadius: '6px',
                                    backgroundColor: '#4F46E5', color: 'white',
                                    cursor: 'pointer', fontSize: '13px',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    whiteSpace: 'nowrap', flexShrink: 0,
                                }}
                            >
                                <FontAwesomeIcon icon={faPaperPlane} size="sm"/> {t('gps.assets.sendAction')}
                            </button>
                            <div className="d-flex gap-1" style={{flexShrink: 0}}>
                                <button
                                    onClick={() => setViewMode('grid')}
                                    title="Grid view"
                                    style={{
                                        padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: '6px',
                                        backgroundColor: viewMode === 'grid' ? '#1E293B' : '#F8FAFC',
                                        color: viewMode === 'grid' ? 'white' : '#64748B',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <FontAwesomeIcon icon={faGrip}/>
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    title="List view"
                                    style={{
                                        padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: '6px',
                                        backgroundColor: viewMode === 'list' ? '#1E293B' : '#F8FAFC',
                                        color: viewMode === 'list' ? 'white' : '#64748B',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <FontAwesomeIcon icon={faList}/>
                                </button>
                            </div>
                        </div>
                    </div>

                    {viewMode === 'grid' ? (
                        <div className="d-flex flex-wrap gap-3">
                            {filteredAssets.map(renderAssetCard)}
                        </div>
                    ) : (
                        <div style={{backgroundColor: 'white', borderRadius: '8px', border: '1px solid #E2E8F0', overflow: 'hidden'}}>
                            <div style={{overflowX: 'auto', WebkitOverflowScrolling: 'touch'}}>
                            <table style={{width: '100%', borderCollapse: 'collapse', minWidth: '600px'}}>
                                <thead>
                                    <tr style={{backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0'}}>
                                        <th style={{padding: '10px 12px', textAlign: 'left', fontSize: '12px', color: '#64748B', fontWeight: '600', whiteSpace: 'nowrap'}}>{t('gps.assets.name')}</th>
                                        <th style={{padding: '10px 12px', textAlign: 'left', fontSize: '12px', color: '#64748B', fontWeight: '600', whiteSpace: 'nowrap'}}>{t('gps.assets.idDni')}</th>
                                        <th style={{padding: '10px 12px', textAlign: 'left', fontSize: '12px', color: '#64748B', fontWeight: '600', whiteSpace: 'nowrap'}}>{t('gps.assets.activeDevice')}</th>
                                        <th style={{padding: '10px 12px', textAlign: 'left', fontSize: '12px', color: '#64748B', fontWeight: '600', whiteSpace: 'nowrap'}}>{t('gps.assets.position')}</th>
                                        <th style={{padding: '10px 12px', textAlign: 'left', fontSize: '12px', color: '#64748B', fontWeight: '600', whiteSpace: 'nowrap'}}>{t('gps.assets.lastSeen')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAssets.map(renderAssetRow)}
                                </tbody>
                            </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Groups */}
                <div>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <h6 style={{fontWeight: '600', margin: 0}}>{t('gps.assets.groups')}</h6>
                        <button
                            onClick={() => setShowGroupModal(true)}
                            style={{
                                padding: '6px 12px', borderRadius: '6px', border: 'none',
                                backgroundColor: '#1E293B', color: 'white', fontSize: '13px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '4px'
                            }}
                        >
                            <FontAwesomeIcon icon={faPlus} size="sm"/> {t('gps.assets.newGroup')}
                        </button>
                    </div>

                    <div className="d-flex flex-wrap gap-3">
                        {groups.map(group => (
                            <div key={group.id} style={{...cardStyle, width: 'min(280px, 100%)'}}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div style={{fontWeight: '600', fontSize: '14px'}}>{group.name}</div>
                                    <FontAwesomeIcon
                                        icon={faTrash}
                                        color="#EF4444"
                                        size="sm"
                                        style={{cursor: 'pointer'}}
                                        onClick={() => deleteGroup(group.id)}
                                    />
                                </div>
                                <div style={{fontSize: '12px', color: '#64748B', marginBottom: '8px'}}>
                                    {t('gps.assets.membersCount', {count: group.members?.length || 0})}
                                </div>
                                <button
                                    onClick={() => openMembersModal(group)}
                                    style={{
                                        width: '100%', padding: '6px', borderRadius: '4px',
                                        border: '1px solid #CBD5E1', backgroundColor: '#F8FAFC',
                                        fontSize: '13px', cursor: 'pointer'
                                    }}
                                >
                                    {t('gps.assets.manageMembers')}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
